// redux/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../utils/api";

// Check for stored user data
const getUserFromStorage = () => {
  const storedUser = localStorage.getItem("user");
  if (storedUser) {
    try {
      const userData = JSON.parse(storedUser);
      if (userData?.token) {
        api.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${userData.token}`;
      }
      return userData;
    } catch (e) {
      // If there's an error parsing the stored user, remove it
      localStorage.removeItem("user");
      return null;
    }
  }
  return null;
};

// Async thunk for login
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ url = "/loginAdmin", credentials }, { rejectWithValue }) => {
    try {
      const response = await api.post(url, credentials);

      // Check if response has the expected structure
      if (response?.data?.data) {
        const userData = {
          token: response.data.data.token,
          user: response.data.data,
        };

        // Set token in localStorage and axios headers
        localStorage.setItem("user", JSON.stringify(userData));
        api.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${userData.token}`;

        return userData;
      } else {
        return rejectWithValue("بيانات الاستجابة غير صالحة");
      }
    } catch (error) {
      let errorMessage = "فشل تسجيل الدخول";

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// Action to update user data in store after successful API call
const updateUserData = (state, action) => {
  const updatedUserData = {
    ...state.userData,
    user: {
      ...state.userData.user,
      ...action.payload,
    },
  };

  state.userData = updatedUserData;
  // Update localStorage with new user data
  localStorage.setItem("user", JSON.stringify(updatedUserData));
};

// Async thunk for logout
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      // If you need to call an API to logout
      // await api.post('/logout');

      localStorage.removeItem("user");
      delete api.defaults.headers.common["Authorization"];
      return null;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "فشل تسجيل الخروج"
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    userData: getUserFromStorage(),
    isAuthenticated: !!getUserFromStorage(),
    loading: false,
    error: null,
  },
  reducers: {
    resetAuthError(state) {
      state.error = null;
    },
    setAuthError(state, action) {
      state.error = action.payload;
    },
    updateUserProfile(state, action) {
      const updatedUserData = {
        ...state.userData,
        user: {
          ...state.userData.user,
          ...action.payload,
        },
      };

      state.userData = updatedUserData;
      // Update localStorage with new user data
      localStorage.setItem("user", JSON.stringify(updatedUserData));
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.userData = action.payload;
        state.isAuthenticated = true;
        state.loading = false;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "فشل تسجيل الدخول";
      })
      // Logout cases
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.userData = null;
        state.isAuthenticated = false;
        state.loading = false;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "فشل تسجيل الخروج";
      });
  },
});

export const { resetAuthError, setAuthError, updateUserProfile } =
  authSlice.actions;
export default authSlice.reducer;
